import * as angular from 'angular';
import * as _ from 'lodash';

import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { SorterColumnDataType } from '../../../ng2/shared/constants/sorter-column-data-type.constant';
import { IStudent } from '../../../ng2/shared/typings/interfaces/student.interface';
import { SdcStateService } from './../sdc-state/sdc-state.service';

const ignore = ['$inject', '$$moduleName'];

// Hack to make these injected services available from service class methods
let _SorterColumn;
let _UtilitiesService;
let _SdcStateService;

export class SdcStoreValidator {
  static $inject = ['SorterColumn', 'UtilitiesService', 'SdcStateService'];

  constructor(
    private SorterColumn,
    private UtilitiesService: UtilitiesService,
    private SdcStateService: SdcStateService,
  ) {
    _SorterColumn = SorterColumn;
    _UtilitiesService = UtilitiesService;
    _SdcStateService = SdcStateService;
  }

  validate(previousState, newState, affectedPaths) {
    let error = null;

    // Iterate thru each static method
    const hasError = _.find(_.keys(SdcStoreValidator), functionName => {
      if (_.includes(ignore, functionName)) return false;
      error = SdcStoreValidator[functionName](previousState, newState, affectedPaths);
      return _.isError(error);
    });

    // We need to console.warn and then swallow the error here because flux-angular always rolls back a change if
    // there is an error
    if (hasError) {
      console.warn(error.message);
    }

    return; // swallowing the error
  }

  static doAllEnumFiltersContainValidValues(previousState, newState, affectedPaths) {
    // Find all of the filters that have invalid values
    const colsWithInvalidValues = _.filter(_.keys(newState.filter), colName => {
      const col = _SorterColumn.getByColumnKey(colName);
      if (col.dataType !== SorterColumnDataType.ENUM) return false;

      const allowedValues = col.dataTypeOptions.values;

      let invalidValues = _.difference(newState.filter[colName], allowedValues);

      if (col.dataTypeOptions.canBeNull) {
        invalidValues = _.difference(invalidValues, [null]);
      }

      return invalidValues.length > 0;
    });

    if (colsWithInvalidValues.length > 0) {
      return new Error(
        `Tried to filter an ENUM column by values that are not listed in their SorterColumn ` +
          `definition. This can cause AgGrid filter issues. Add any missing ENUM values to the SorterColumn constant. ` +
          `Columns affected: "${colsWithInvalidValues.join('", "')}".`,
      );
    }
  }

  static doAllEnumColumnsContainValidValues(previousState, newState, affectedPaths) {
    // only run this when the students load or are changed
    if (!_.find(affectedPaths, path => angular.equals(path, ['students']))) return;

    const enumColumnKeys = _.filter(
      newState.columnKeys,
      (colName: string) => _SorterColumn.getByColumnKey(colName).dataType === SorterColumnDataType.ENUM,
    );

    function isValueIncludedInEnum(colName, value) {
      const col = _SorterColumn.getByColumnKey(colName);
      if (_.isNull(value) && col.dataTypeOptions.canBeNull) return true;
      return _.includes(col.dataTypeOptions.values, value);
    }

    let errors = [];
    for (let student of newState.flattenedStudents) {
      for (let col of enumColumnKeys) {
        if (!isValueIncludedInEnum(col, student[col])) {
          errors.push(`student ${student._id} has value "${student[col]}" in column ${col}`);
          break;
        }
      }
      if (errors.length > 10) break;
    }

    if (errors.length > 0) {
      return new Error(
        `Some students have invalid values in ENUM columns: ${errors.join('; ')}. Some additional errors may have ` +
          `been suppressed.`,
      );
    }
  }

  static doesSortOrderMatchColumns(previousState, newState, affectedPaths) {
    // Only run when sort order or columnKeys changes
    if (!_.find(affectedPaths, path => angular.equals(path, ['sort']) || angular.equals(path, ['columnKeys']))) {
      return;
    }

    const sortCols = _.map(newState.sort, 'colId');
    const sortColsInColKeys = _.intersection(newState.columnKeys, sortCols);
    if (sortColsInColKeys.length !== sortCols.length) {
      return new Error(
        `Columns are present in sort but not columnKeys: ${_.difference(sortCols, newState.columnKeys).join(', ')}`,
      );
    }
  }

  static doesCurrentActivityStateExist(previousState, newState, affectedPaths) {
    // Only run when currentActivity changes
    if (!_.find(affectedPaths, path => angular.equals(path, ['ui', 'currentActivity']))) {
      return;
    }

    if (
      newState.ui.currentActivity.state &&
      !_SdcStateService.determineSdcActivityFromState(newState.ui.currentActivity.state)
    ) {
      return new Error(`SDCStore value for ui.currentActivity.state does not exist in the sdcActivityConstant`);
    }
  }

  static studentsHaveLoadedProjection(previousState, newState, affectedPaths) {
    const requiredPaths = newState.projection;
    let errors = [];

    _.each(newState.students, (student: IStudent) => {
      _.each(requiredPaths, path => {
        let propValue = null;
        propValue = _UtilitiesService.getFieldByPath(student, path);
        if (propValue === undefined) {
          errors.push(`student ${student._id} is missing property "${path}"`);
        }
      });
      if (errors.length > 10) return;
    });

    if (errors.length > 0) {
      return new Error(
        `Some students have invalid projections ${errors.join('; ')}. Some additional errors may have ` +
          `been suppressed.`,
      );
    }
  }

  static doAllHiddenColumnKeysAppearInColumnKeys(previousState, newState, affectedPaths) {
    function affectsHiddenColumnKeysOrColumnKeys(path) {
      return angular.equals(path, ['hiddenColumnKeys']) || angular.equals(path, ['columnKeys']);
    }

    if (!_.find(affectedPaths, affectsHiddenColumnKeysOrColumnKeys)) {
      return;
    }

    const diff = _.difference(newState.hiddenColumnKeys, newState.columnKeys);
    if (diff.length !== 0) {
      return new Error(`columns [${diff.join(', ')}] are hidden but are not loaded`);
    }
  }
}

angular.module('SdcFluxModule').service('SDCStoreValidator', SdcStoreValidator);
